import React, { useState } from 'react';
import { FaTimes, FaPlay, FaEye, FaEyeSlash } from 'react-icons/fa';
import { showSuccess, showError } from '../../utils/toastUtils';

export default function LessonModal({ isOpen, data, onDataChange, onClose, onSubmit }) {
    const [showPreview, setShowPreview] = useState(false);

    if (!isOpen) return null;

    const getEmbedUrl = (url) => {
        if (!url) return null;
        const ytRegex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const ytMatch = url.match(ytRegex);
        if (ytMatch && ytMatch[2].length === 11) {
            return `https://www.youtube.com/embed/${ytMatch[2]}`;
        }
        return null;
    };

    const embedUrl = getEmbedUrl(data.recording_url);

    return (
        <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
            <div className={`bg-white rounded-[2rem] shadow-2xl overflow-hidden animated fadeInUp transition-all duration-300 my-auto flex flex-col max-h-[95vh] w-full ${showPreview && embedUrl ? 'max-w-4xl' : 'max-w-lg'}`}>
                {/* Fixed Header */}
                <div className="p-6 md:p-8 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
                    <h3 className="text-xl font-bold text-gray-900">
                        {data.id ? 'Edit Lesson' : 'Add New Lesson'}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2">
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="flex flex-col md:flex-row overflow-hidden flex-grow">
                    <form onSubmit={onSubmit} className={`flex flex-col overflow-hidden ${showPreview && embedUrl ? 'w-full md:w-1/2' : 'w-full'} transition-all`}>
                        {/* Scrollable Form Body */}
                        <div className="p-6 md:p-8 space-y-6 overflow-y-auto custom-scrollbar flex-grow">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Title</label>
                                <input
                                    value={data.title}
                                    onChange={(e) => onDataChange({ ...data, title: e.target.value })}
                                    className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                                    placeholder="Lesson 01 - Introductions"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Duration</label>
                                    <input
                                        value={data.duration}
                                        onChange={(e) => onDataChange({ ...data, duration: e.target.value })}
                                        className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                                        placeholder="02:30:15"
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-xs font-bold text-gray-400 uppercase">Video URL</label>
                                    <div className="flex gap-3">
                                        {embedUrl && (
                                            <button
                                                type="button"
                                                onClick={() => setShowPreview(!showPreview)}
                                                className={`text-[10px] font-black uppercase flex items-center gap-1 transition-colors ${showPreview ? 'text-blue-600' : 'text-gray-400 hover:text-blue-600'}`}
                                            >
                                                {showPreview ? <><FaEyeSlash size={10} /> Hide Preview</> : <><FaEye size={10} /> Show Preview</>}
                                            </button>
                                        )}
                                        {data.recording_url && (
                                            <a
                                                href={data.recording_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[10px] font-black text-blue-600 uppercase hover:underline flex items-center gap-1"
                                            >
                                                Open External <FaPlay size={8} className="translate-x-0.5" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <input
                                    value={data.recording_url || ''}
                                    onChange={(e) => onDataChange({ ...data, recording_url: e.target.value })}
                                    className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                                    placeholder="https://..."
                                />
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block text-xs font-bold text-gray-400 uppercase">PDF Document URL</label>
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('pdf-upload').click()}
                                        className="text-[10px] font-black text-blue-600 uppercase hover:underline"
                                    >
                                        Upload Local PDF
                                    </button>
                                    <input
                                        id="pdf-upload"
                                        type="file"
                                        accept="application/pdf"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;

                                            const formData = new FormData();
                                            formData.append('pdf_file', file);

                                            try {
                                                const res = await fetch('api/admin/upload_pdf.php', {
                                                    method: 'POST',
                                                    body: formData
                                                });
                                                const result = await res.json();
                                                if (result.status === 'success') {
                                                    onDataChange({ ...data, pdf_url: result.data.url });
                                                } else {
                                                    showSuccess(result.message);
                                                }
                                            } catch (err) {
                                                showError("Upload failed: " + err.message);
                                            }
                                        }}
                                    />
                                </div>
                                <input
                                    value={data.pdf_url || ''}
                                    onChange={(e) => onDataChange({ ...data, pdf_url: e.target.value })}
                                    className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                                    placeholder="https://..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Description</label>
                                <textarea
                                    value={data.description || ''}
                                    onChange={(e) => onDataChange({ ...data, description: e.target.value })}
                                    className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none font-medium resize-none h-24"
                                    placeholder="Add lesson notes, key points, or instructions..."
                                />
                            </div>
                        </div>

                        {/* Fixed Actions */}
                        <div className="p-6 md:p-8 border-t border-gray-100 flex justify-end gap-3 shrink-0 bg-gray-50/50">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-3 font-bold text-gray-400 hover:text-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all active:scale-95"
                            >
                                {data.id ? 'Save Changes' : 'Save Lesson'}
                            </button>
                        </div>
                    </form>

                    {showPreview && embedUrl && (
                        <div className="w-full md:w-1/2 bg-gray-900 flex items-center justify-center relative animated fadeIn min-h-[300px]">
                            <iframe
                                className="w-full aspect-video md:h-full"
                                src={embedUrl}
                                title="Video Preview"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                            <button
                                onClick={() => setShowPreview(false)}
                                className="absolute top-4 right-4 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/80 transition-all"
                            >
                                <FaTimes size={14} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
